/**
 * The popup wizard's saved screens (a product's `display.steps`) laid over the
 * questions' current order.
 *
 * A saved screen says which questions share a screen and what the screen is
 * called; it does NOT fix their order. The option set does: a question moved
 * in the set moves in the wizard too, the moment the set is published — the
 * saved list was written in the order the set had when the product was
 * connected, and read as the order it froze the shop on that one.
 *
 * Neighbours (in the current order) that were saved on the same screen share
 * one; a saved screen whose questions are no longer next to each other splits,
 * and only its first piece keeps the title. A question no saved screen names
 * (added to the set after the product was saved) is a screen of its own at its
 * place, marked `saved: false` for the caller to group further.
 */
export interface SavedWizardStep {
  title?: string;
  optionIds?: string[];
}

export interface WizardScreen {
  title?: string;
  optionIds: string[];
  /** False for a question no saved screen names. */
  saved: boolean;
}

export function orderWizardSteps(order: readonly string[], steps: readonly SavedWizardStep[]): WizardScreen[] {
  const home = new Map<string, number>();
  steps.forEach((step, index) => {
    for (const id of step.optionIds ?? []) if (!home.has(id)) home.set(id, index);
  });
  const titled = new Set<number>();
  const screens: WizardScreen[] = [];
  let lastHome: number | undefined;
  for (const id of order) {
    const index = home.get(id);
    if (index === undefined) {
      screens.push({ optionIds: [id], saved: false });
      lastHome = undefined;
      continue;
    }
    if (index === lastHome) {
      screens[screens.length - 1]!.optionIds.push(id);
      continue;
    }
    const title = steps[index]?.title?.trim();
    screens.push({ ...(title && !titled.has(index) ? { title } : {}), optionIds: [id], saved: true });
    titled.add(index);
    lastHome = index;
  }
  return screens;
}
